/**
 * =============================================================================
 * ROASTBREW - UNSUPPORTED BROWSER GATE
 * =============================================================================
 *
 * Wraps the ENTIRE application. When the playback-capability verdict says this
 * browser has NO DRM ENGINE AT ALL (player === 'unsupported': Apple hardware whose
 * FairPlay probe failed), the app is replaced with a prompt asking the user to
 * continue in Safari.
 *
 * THIS IS NOW A RARE CASE. It does NOT mean "iOS non-Safari". iOS Chrome, Firefox
 * and Edge are WKWebView shells that inherit Safari's FairPlay CDM; they have been
 * tested on device and play fine, and the gate deliberately does not block them.
 * See the `blocked` expression at the bottom of this file for the full history.
 *
 * WHY A GATE AND NOT A PER-PAGE CHECK:
 * Every page in the app is reachable from every other page. Gating once, above
 * the router, means no page component ever has to know this state exists — they
 * simply never render on an unsupported browser.
 *
 * PLACEMENT (App.jsx):
 *   <BrowserRouter>                     ← useLocation() needs this above us
 *     <PlaybackCapabilityProvider>      ← usePlaybackCapability() needs this above us
 *       <UnsupportedBrowserGate>        ← HERE
 *         <AppInner/>                   ← never mounts while blocked, so no auth
 *       </UnsupportedBrowserGate>          or catalog fetches fire
 *     </PlaybackCapabilityProvider>
 *   </BrowserRouter>
 *
 * This sits OUTSIDE ThemeProvider and AuthContext by design, so it cannot call
 * useTheme() or useAuth(). Its colours come from publicTheme.js instead — a
 * plain module import, which is the only mechanism that reaches a component
 * mounted above every provider in the tree. VerifyEmailPage, ResetPasswordPage,
 * AuthUI and LandingPageObs all read the same file, so the pre-auth surface is
 * re-skinned in one place.
 *
 * COPY, EXEMPT ROUTES AND BEHAVIOUR TOGGLES LIVE IN GATE_CONFIG BELOW.
 * Colour is no longer there — edit publicTheme.js for that.
 * =============================================================================
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { usePlaybackCapability } from './PlaybackCapabilityContext';
import { T, PublicTheme } from './publicTheme';
import PublicMark from './PublicMark';

// =============================================================================
// GATE_CONFIG — the whole tunable surface of this component
// =============================================================================

const GATE_CONFIG = {
  // ---------------------------------------------------------------------------
  // ROUTES THAT ARE NEVER GATED
  // ---------------------------------------------------------------------------
  // These are opened from email links, and an email link opens in the user's
  // DEFAULT browser — which for these users is the very browser we are blocking.
  // Neither page plays video or needs a CDM, so gating them would strand people
  // mid-signup with no way through. Matched by pathname prefix.
  exemptPaths: ['/verify-email', '/reset-password'],

  // ---------------------------------------------------------------------------
  // ESCAPE HATCH
  // ---------------------------------------------------------------------------
  // ?nogate=1 disables the gate for the session. Capability detection can be
  // wrong, and "user sees a broken player" is a far cheaper failure than "user
  // cannot open the site at all". Set to null to remove the hatch entirely.
  bypassParam: 'nogate',

  // ---------------------------------------------------------------------------
  // BEHAVIOUR
  // ---------------------------------------------------------------------------
  behaviour: {
    // Attempt Safari's undocumented x-safari-https: scheme on the primary button.
    // Confirmed working on iOS 15/17/18 and reported on 26; known NOT to work on
    // iOS 16. Apple documents none of this and it cannot be feature-detected, so
    // it is strictly an enhancement — the copy-link path below is the real
    // mechanism. Set false to ship copy-only.
    trySafariScheme: true,

    // If the page is still visible this long after the scheme attempt, the jump
    // did not happen and we reveal the copy fallback.
    schemeTimeoutMs: 1500,

    // How long the "Copied" confirmation stays up.
    copiedFeedbackMs: 2500,
  },

  // ---------------------------------------------------------------------------
  // COPY — keyed by hardware class from the capability verdict.
  // `device` is HARDWARE, not viewport: an iPad in a narrow window is still
  // 'tablet'. It never affects whether the gate fires, only what it says.
  // ---------------------------------------------------------------------------
  copy: {
    phone: {
      headline: 'Open this in Safari',
      body: 'Protected video needs Safari on iPhone. Other browsers on iOS can’t play it — Apple only provides the required playback support to Safari.',
      primaryButton: 'Open in Safari',
      fallbackIntro: 'Copy the link, then paste it into Safari:',
    },
    tablet: {
      headline: 'Open this in Safari',
      body: 'Protected video needs Safari on iPad. Other browsers on iOS can’t play it — Apple only provides the required playback support to Safari. It also works in any browser on a computer.',
      primaryButton: 'Open in Safari',
      fallbackIntro: 'Copy the link, then paste it into Safari:',
    },
    // Defensive only: 'unsupported' currently requires Apple mobile hardware, so
    // this should be unreachable. Kept so the component can never render blank.
    desktop: {
      headline: 'This browser can’t play protected video',
      body: 'Your browser doesn’t provide the playback support this content requires. Safari, Chrome, Firefox, or Edge on a computer will work.',
      primaryButton: 'Open in Safari',
      fallbackIntro: 'Copy the link and open it in a supported browser:',
    },
  },

  // Shared strings, device-independent.
  strings: {
    copyButton: 'Copy link',
    copiedButton: 'Copied',
    copyHint: 'Tap and hold the address above to copy it.',
  },
};

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Rewrite an https:// URL to Safari's x-safari-https: scheme. Safari registers
 * this in its bundle under "Safari Start Page"; it opens the URL in Safari
 * regardless of which browser is set as default.
 * Returns null when the URL isn't https, so we never emit a malformed scheme.
 */
function toSafariScheme(href) {
  if (typeof href !== 'string') return null;
  if (!href.startsWith('https://')) return null;
  return 'x-safari-https://' + href.slice('https://'.length);
}

/** Prefix match, so /verify-email and /reset-password match with any query string. */
function isExemptPath(pathname, exemptPaths) {
  return (exemptPaths || []).some(
    (p) => pathname === p || pathname.startsWith(p + '/')
  );
}

// Once ?nogate=1 has been seen, the bypass sticks for the rest of this SPA
// session. Without this latch the gate would slam shut on the first in-app
// navigation, because the query string does not survive a route change — the
// escape hatch would work exactly once and then trap the user again.
let BYPASS_LATCHED = false;

function hasBypassParam(search, param) {
  if (!param) return false;
  if (BYPASS_LATCHED) return true;
  try {
    if (new URLSearchParams(search).get(param) === '1') {
      BYPASS_LATCHED = true;
      return true;
    }
  } catch (e) {
    return false;
  }
  return false;
}

// =============================================================================
// THE PROMPT SCREEN
// =============================================================================

function UnsupportedBrowserPrompt({ device }) {
  const copy = GATE_CONFIG.copy[device] || GATE_CONFIG.copy.phone;
  const { trySafariScheme, schemeTimeoutMs, copiedFeedbackMs } = GATE_CONFIG.behaviour;

  // The exact page the user asked for, deep link intact. Page URLs carry no
  // signed token (the CDN ?t= lives on media URLs only), so this is safe to copy.
  const href = typeof window !== 'undefined' ? window.location.href : '';
  const safariUrl = toSafariScheme(href);

  // The fallback starts hidden only if we have a scheme attempt to make. With
  // the attempt disabled — or on a non-https origin — it is the primary path
  // and must be visible immediately.
  const [showFallback, setShowFallback] = useState(!(trySafariScheme && safariUrl));
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const timersRef = useRef([]);
  const cleanupsRef = useRef([]);

  useEffect(() => () => {
    timersRef.current.forEach(clearTimeout);
    cleanupsRef.current.forEach((fn) => { try { fn(); } catch (e) { /* noop */ } });
  }, []);

  // A successful scheme jump backgrounds this page. If we are still visible when
  // the timer fires, nothing happened — reveal the copy path. There is no way to
  // feature-detect a URL scheme (deliberately: it would be a fingerprinting
  // vector), so observing the page is the only signal available.
  const attemptSafari = useCallback(() => {
    if (!safariUrl) { setShowFallback(true); return; }

    let left = false;
    const onHide = () => { left = true; };
    const cleanup = () => {
      document.removeEventListener('visibilitychange', onHide);
      window.removeEventListener('pagehide', onHide);
    };
    document.addEventListener('visibilitychange', onHide);
    window.addEventListener('pagehide', onHide);
    cleanupsRef.current.push(cleanup);

    // Must be a direct user gesture; iOS has long blocked custom-scheme
    // navigation from non-gesture contexts.
    window.location.href = safariUrl;

    const timer = setTimeout(() => {
      cleanup();
      if (!left && document.visibilityState === 'visible') setShowFallback(true);
    }, schemeTimeoutMs);
    timersRef.current.push(timer);
  }, [safariUrl, schemeTimeoutMs]);

  const copyLink = useCallback(async () => {
    try {
      if (!navigator.clipboard || !navigator.clipboard.writeText) throw new Error('no clipboard');
      await navigator.clipboard.writeText(href);
      setCopied(true);
      setCopyFailed(false);
      const timer = setTimeout(() => setCopied(false), copiedFeedbackMs);
      timersRef.current.push(timer);
    } catch (e) {
      // Absent in some in-app webviews and on non-secure origins. Fall back to
      // showing the URL so it can be long-pressed and copied by hand.
      setCopyFailed(true);
    }
  }, [href, copiedFeedbackMs]);

  return (
    <div
      className="rb-public"
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '24px',
      }}
    >
      <PublicTheme />
      <div className="rb-ambient" />

      <PublicMark size="lg" style={{ marginBottom: 'var(--s-5)' }} />

      <div
        className="rb-enter"
        style={{
          textAlign: 'center', maxWidth: 'var(--m-form)', width: '100%',
          position: 'relative', zIndex: 1,
        }}
      >
        {/* Compass glyph — Safari's shape, without reproducing the icon */}
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%',
          background: `${T.gold}14`, border: `.5px solid ${T.gold}33`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto var(--s-3)',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
            stroke={T.gold} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M15.5 8.5l-2 5-5 2 2-5z" />
          </svg>
        </div>

        <h1 className="rb-h">{copy.headline}</h1>

        <p className="rb-note" style={{ marginBottom: 'var(--s-4)' }}>
          {copy.body}
        </p>

        {/* Primary action — only when we have a scheme attempt available */}
        {!showFallback && (
          <button className="rb-btn rb-btn--gold rb-btn--block" onClick={attemptSafari}>
            {copy.primaryButton}
          </button>
        )}

        {/* Fallback — the path that always works */}
        {showFallback && (
          <div style={{ animation: 'rbFadeIn 0.4s ease' }}>
            <p className="rb-note" style={{ marginBottom: 'var(--s-2)' }}>
              {copy.fallbackIntro}
            </p>

            {/* Selectable, so a long-press then Copy works where the API doesn't */}
            <div style={{
              padding: '14px 16px', marginBottom: 'var(--s-2)',
              background: T.field, border: `.5px solid ${T.fieldEdge}`, borderRadius: '2px',
              fontSize: '13px', color: T.parchment, fontFamily: T.body,
              wordBreak: 'break-all', textAlign: 'left', lineHeight: 1.5,
              userSelect: 'all', WebkitUserSelect: 'all',
            }}>
              {href}
            </div>

            <button
              className={`rb-btn rb-btn--block ${copied ? 'rb-btn--gold' : 'rb-btn--ghost'}`}
              onClick={copyLink}
            >
              {copied ? GATE_CONFIG.strings.copiedButton : GATE_CONFIG.strings.copyButton}
            </button>

            {copyFailed && (
              <p className="rb-note" style={{ marginTop: 'var(--s-2)' }}>
                {GATE_CONFIG.strings.copyHint}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// THE GATE
// =============================================================================

export default function UnsupportedBrowserGate({ children }) {
  const { player, device } = usePlaybackCapability();
  const { pathname, search } = useLocation();

  // BLOCK ON CAPABILITY ONLY.
  //
  //   player === 'unsupported'  This browser has no DRM engine at all — Apple
  //                             hardware whose FairPlay probe failed. Technical,
  //                             unarguable, and rare.
  //
  // WHAT USED TO BE HERE, AND WHY IT IS GONE:
  // This also blocked `isIosNonSafari`, on the documented assumption that
  // Chrome/Firefox/Edge on iOS had no CDM. That assumption was WRONG, and it has
  // now been TESTED on device: iOS Chrome reports player 'videojs', routes to the
  // FairPlay mobile page, and plays the muxed SAMPLE-AES package with audio —
  // indistinguishable from iOS Safari. They are WKWebView shells, so they inherit
  // Safari's FairPlay CDM. There was never anything to block.
  //
  // (The symptom that made it look broken was a UI bug: a shared touch/click
  // dedupe timestamp in the mobile player swallowed the second tap of a
  // reveal-then-press sequence, so the MUTE button could not be pressed. Video
  // was playing correctly the whole time.)
  //
  // `isIosNonSafari` is still computed and exported — it is accurate and it is
  // the switch to reach for if one specific iOS browser ever does break. It is
  // simply not read here. To re-enable a policy block, add it back to this one
  // expression; nothing else in the app needs to change.
  //
  // NEVER gate on isAppleMobile: it is TRUE on iPhone/iPad Safari, which works.
  const blocked = player === 'unsupported';

  if (!blocked) return children;
  if (isExemptPath(pathname, GATE_CONFIG.exemptPaths)) return children;
  if (hasBypassParam(search, GATE_CONFIG.bypassParam)) return children;

  return <UnsupportedBrowserPrompt device={device} />;
}
